import { kErrorDisplayingResponseMessage } from '@/constants/constants-strings'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import ErrorMessageBubble from '../../events/components/ErrorMessageBubble'
import { selectUserQueryEventExpanded } from '../store/selectors'
import { useEffect, useRef } from 'react'
import { AgentEventsActions } from '../store/slice'
import { ChevronDown, ChevronRight } from '@/assets/icons'
import clsx from 'clsx'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'

type AgentUserQueryEventBubbleProps = {
  conversationId: string
  eventId: string
}

/**
 * Agent User Query Event Bubble
 *
 * A component to display events of type USER_QUERY.
 */
export default function AgentUserQueryEventBubble(props: AgentUserQueryEventBubbleProps) {
  const { conversationId, eventId } = props
  const dispatch = useAppDispatch()

  // Get the viewport size in pixels to use in the useEffect below
  const { width: viewportWidth, height: viewportHeight } = useViewportSizeInPixels()

  const event = useAppSelector((state: RootState) => state.agentEventsState.events[conversationId]?.[eventId])

  // Get the expanded state from the store. It can be:
  // - undefined: Not measured yet OR content fits and does not need collapsing
  // - false: Measured, content overflows, and is currently collapsed
  // - true: Measured, content overflows, and is currently expanded
  const expanded = useAppSelector((state) => selectUserQueryEventExpanded(state, { conversationId, eventId }))

  // If expanded is defined, we've already measured previously and established that the content exceeds the original height
  const contentExceedsOriginalHeight = expanded !== undefined

  // A ref to the message container to measure its height
  const messageContainerRef = useRef<HTMLDivElement>(null)

  /* 
   * Set User Query Event Bubble Expanded State

   * Check if the height of the message text exceeds the height of the container.
   * If so, set the state of the bubble to collapsed, and we'll provide a toggle to expand it.
   */
  useEffect(() => {
    // If we already established that the content exceeds the original height, skip
    if (contentExceedsOriginalHeight) return

    const checkHeight = () => {
      if (!messageContainerRef.current) return

      const containerHeight = messageContainerRef.current.clientHeight
      const contentHeight = messageContainerRef.current.scrollHeight

      // If the content height exceeds the container height, we'll collapse the bubble
      if (contentHeight > containerHeight) {
        dispatch(AgentEventsActions.setUserQueryEventExpanded({ conversationId, eventId, expanded: false }))
      }
    }

    // Run the check on mount and when viewport changes
    checkHeight()
  }, [contentExceedsOriginalHeight, conversationId, eventId, dispatch, viewportWidth, viewportHeight])

  if (!event) {
    console.error('Event not found')
    return <ErrorMessageBubble message={kErrorDisplayingResponseMessage} />
  }

  const trimmedText = event.value.trim() || ''

  const messageHeader = 'You'

  return (
    <div className="px-4 rounded-lg text-base whitespace-pre-wrap compatible-word-break bg-transparent">
      {/* Header */}
      <div className="font-bold text-xl mb-1">{messageHeader}</div>

      {/* Message Content */}
      <div
        ref={messageContainerRef}
        className={clsx('text-sm leading-6 mb-1 overflow-hidden compatible-word-break', {
          'max-h-48': !expanded,
          'user-query-text-fade': !expanded && contentExceedsOriginalHeight,
        })}
      >
        {trimmedText}
      </div>

      {/* Expand / Collapse Toggle - shown if we've measured the content and it exceeds the original height of the container */}
      {contentExceedsOriginalHeight && (
        <>
          <div className="border-t pt-2 flex">
            <div
              className="px-1 text-sm text-brand-neutral-500 flex items-center gap-x-1 cursor-pointer rounded-md hover:bg-brand-neutral-100"
              onClick={() => dispatch(AgentEventsActions.setUserQueryEventExpanded({ conversationId, eventId, expanded: !expanded }))}
            >
              {expanded ? (
                <>
                  Show less <ChevronDown className="h-4 w-4" />
                </>
              ) : (
                <>
                  Show more <ChevronRight className="h-4 w-4" />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
